import { TutorialDefinition } from '../TutorialEngine.js';
import { OrbitsRenderOptions } from './OrbitsTutorialAdapter.js';
import { COLORS } from '../../rendering/Renderer.js';

type OrbitsScene = {
    gameState: string;
    renderOptions: OrbitsRenderOptions;
    annotations: TutorialDefinition['scenes'][number]['annotations'];
    animate?: {
        interval: number;
        loop: boolean;
        frames: string[];
    };
};

// Grid: 6/1-3:1D1,2A2,3E3
// Full solution: WC,C1C2,E1F1,E4E5,oSE5

const scenes: OrbitsScene[] = [
    // Scene 1: Goal — what a solved puzzle looks like
    {
        gameState: 'WC,C1C2,E1F1,E4E5,oSE5',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'In Orbits, guide a ship\nthrough all digits in order', cell: 'C0', anchor: 'center', color: COLORS.teal },
        ],
        animate: {
            interval: 2000,
            loop: true,
            frames: [
                'WC,C1C2,E1F1,E4E5,oSE5',
                'WC,C1C2,E1F1,E4E5,oSE5+D1',
                'WC,C1C2,E1F1,E4E5,oSE5+A2',
                'WC,C1C2,E1F1,E4E5,oSE5+E3',
            ],
        },
    },

    // Scene 2: Orbit rings and launcher
    {
        gameState: 'NC',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'These are the\norbit rings', cell: 'C0', anchor: 'center' },
            { type: 'text-label', text: 'Here\'s the\nlauncher', edge: '3233', anchor: 'right' },
            { type: 'pulse', edge: '3233', color: COLORS.teal },
        ],
    },

    // Scene 3: Click launcher to flip it, click digits to see path
    {
        gameState: 'NC',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Click the center arrow\nto flip direction', cell: 'B0', anchor: 'center' },
            { type: 'text-label', text: 'Click a digit to\nsee the ship\'s path', cell: 'D5', anchor: 'center' },
            { type: 'pulse', cell: 'D1', color: COLORS.teal },
            { type: 'pulse', point: '33', color: COLORS.teal },
        ],
        animate: {
            interval: 2000,
            loop: true,
            frames: ['NC', 'NC+D1', 'NX+D1'],
        },
    },

    // Scene 4: Click orbit edges to place gates, click center edges to move launcher
    {
        gameState: 'WX,C1C2',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Click orbit edges\nto place gates', cell: 'E0', anchor: 'center' },
            { type: 'text-label', text: 'Click center edges\nto move the launcher', cell: 'C4', anchor: 'center' },
            { type: 'pulse', edge: '2333', color: COLORS.teal },
            { type: 'pulse', edge: '4243', color: COLORS.teal },
        ],
        animate: {
            interval: 2000,
            loop: true,
            frames: ['WX,C1C2', 'NX,C1C2', 'WX,C1C2,D2E2'],
        },
    },

    // Scene 5: Series of gates are followed, gates on both sides are ignored
    {
        gameState: 'WC,C1C2,C0C1,D4D5,oSD5',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Ship follows\nseries of gates', cell: 'E0', anchor: 'center' },
            { type: 'text-label', text: 'Moves straight\nbetween gates', cell: 'B5', anchor: 'center' },
        ],
    },

    // Scene 6: conflicts are resolved automatically at placement — a gate that
    // would clash at a corner replaces the old one, and a new exit gate replaces
    // the previous exit. Animate both rather than showing forbidden states.
    {
        gameState: 'WC,A1B1',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'At most one\ngate per corner', cell: 'D0', anchor: 'center', color: COLORS.amber },
            { type: 'text-label', text: 'No gate on\ndigits', cell: 'B2', anchor: 'center', color: COLORS.amber },
            { type: 'text-label', text: 'Only one\nexit gate', cell: 'C5', anchor: 'center', color: COLORS.amber },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: [
                'WC,B0B1',           // a corner gate
                'WC,A1B1',           // perpendicular gate placed → the first clears
                'WC,A1B1,oSE5',      // an exit gate
                'WC,A1B1,oSB5',      // a new exit placed → the previous one clears
            ],
        },
    },

    // Scene 7: Badges and launcher color tell next object hit
    {
        gameState: 'WC,C1C2,E1F1,E4E5',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Badges tell which\nobject the ship hits next', cell: 'C0', anchor: 'center' },
            { type: 'text-label', text: 'Teal = correct', cell: 'B5', anchor: 'near-above', color: COLORS.teal },
            { type: 'text-label', text: 'Amber = incorrect', cell: 'B5', anchor: 'center', color: COLORS.amber },
        ],
        animate: {
            interval: 2000,
            loop: true,
            frames: [
                'WC,C1C2,E1F1,E4E5',
                'WC,C1C2,E1F1,E4E5+D1',
                'WC,C1C2,E1F1,E4E5+A2',
                'WC,C1C2,E1F1,E4E5+E3',
            ],
        },
    },

];

/**
 * Orbits tutorial: 7 scenes on a 6x6 grid with digits 1-3.
 * See docs/tutorials/orbits.md for detailed scene descriptions.
 */
export const ORBITS_TUTORIAL: TutorialDefinition = {
    gameType: 'orbits',
    gridCode: '6/1-3:1D1,2A2,3E3',
    scenes,
};
